import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  TemplateRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { finalize, forkJoin } from 'rxjs';
import moment from 'moment';
import { ChevronDownIconComponent } from '@kuiper/icons';
import { EMPLOYEE_ROUTES } from '../../../../common/constants/employee.routing.constants';
import { ActionList } from '../../../../common/models/action-list.model';
import {
  RequestScheduleDetails,
  RequestScheduleType,
  ScheduleRecurrenceOption,
} from '../../../../common/models/request-schedule.model';
import {
  EVENT_MONTHLY_BY_INTERVAL,
  EVENT_MONTHLY_BY_MONTHS,
  EVENT_MONTHS,
  EVENT_WEEK_DAYS,
} from '../../../../common/constants/recurrence.constants';
import { formatRepeatOnDisplay } from '../../../../common/utils/recurrence.utils';
import { RequestService } from '../../../../common/services/request/request.service';
import { TenantService } from '../../../../common/services/tenant/tenant.service';
import { PermissionsService } from '../../../../common/services/permissions/permissions.service';
import { AppPermissions } from '../../../../common/constants/app-permissions';
import { Permission } from '../../../../common/models/permission.model';
import { ActionListComponent } from '../../../../common/components/action-list/action-list.component';
import { DeleteConfirmationPopupComponent } from '../../../../common/components/delete-confirmation-popup/delete-confirmation-popup.component';
import { ModalServiceService } from '../../../../../ui-components/lib/modal/modal-service.service';
import { OverlayLoaderComponent } from '../../../../common/components/overlay-loader/overlay-loader.component';
import { AvatarComponent } from '../../../../../ui-components/lib/avatar/avatar.component';
import { BadgeComponent } from '../../../../../ui-components/lib/badge/badge.component';
import { LongTextViewComponent } from '../../../../../ui-components/lib/long-text-view/long-text-view.component';
import { StatusPipe } from '../../../../../ui-components/pipes/status/status.pipe';
import { statusEnum } from '../../../../../ui-components/enums/status';
import { ToasterService } from '../../../../../ui-components/services/toast/toast.service';
import {
  buildLegacyMonthlyRepeatOnOptions,
  buildLegacyYearlyRepeatOnOptions,
} from '../schedule-workflow/schedule-workflow.utils';
import { FeatureFlagsService } from '../../../../common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../../common/constants/feature-flags.constants';
import { SectionHeaderComponent } from '../../../../common/components/section-header/section-header.component';
import { ExpandCollapseAllComponent } from '../../../../../ui-components/lib/expand-collapse-all/expand-collapse-all.component';
import { ScheduleRequestsComponent } from './schedule-requests/schedule-requests.component';

@Component({
  selector: 'lib-schedule-details',
  imports: [
    DatePipe,
    TranslocoPipe,
    ChevronDownIconComponent,
    OverlayLoaderComponent,
    AvatarComponent,
    BadgeComponent,
    ActionListComponent,
    DeleteConfirmationPopupComponent,
    LongTextViewComponent,
    StatusPipe,
    ScheduleRequestsComponent,
    SectionHeaderComponent,
    ExpandCollapseAllComponent,
  ],
  templateUrl: './schedule-details.component.html',
  styleUrl: './schedule-details.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ScheduleDetailsComponent {
  readonly EMPLOYEE_ROUTES = EMPLOYEE_ROUTES;
  readonly appPermissions = AppPermissions;

  private readonly requestService = inject(RequestService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly transloco = inject(TranslocoService);
  private readonly tenantService = inject(TenantService);
  private readonly toaster = inject(ToasterService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly modalService = inject(ModalServiceService);
  private readonly featureFlagsService = inject(FeatureFlagsService);

  cancelScheduleModal = viewChild<TemplateRef<unknown>>('cancelScheduleModal');

  isLoading = signal(true);
  isCancelling = signal(false);
  requestId = signal(this.activatedRoute.snapshot.params['requestId'] ?? '');
  scheduleId = signal(this.activatedRoute.snapshot.params['scheduleId'] ?? '');
  scheduleDetails = signal<RequestScheduleDetails | null>(null);
  userPermissions = signal<Permission[]>([]);

  readonly isScheduleRequestsEnabled = signal(
    this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.schedule_workflows_requests
    )
  );
  readonly isExpandCollapseSectionsEnabled = signal(
    this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.form_builder_expand_collapse_sections
    )
  );
  readonly isAllSectionsExpanded = signal(true);

  private readonly timeZoneLocation =
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  private readonly timeZoneOffset = moment().format().split('+')[1];
  readonly timeZoneView = ` - (${this.timeZoneLocation} - GMT +${this.timeZoneOffset})`;

  isActiveSchedule = computed(
    () => this.scheduleDetails()?.state?.value === statusEnum.ACTIVE
  );

  private readonly recurrence = computed(
    () => this.scheduleDetails()?.recurrence ?? null
  );

  private readonly recurrenceType = computed(
    () => this.optionValue(this.recurrence()?.type) ?? null
  );

  readonly scheduleTypeDisplay = computed(() => {
    const details = this.scheduleDetails();
    if (!details?.schedule_type) {
      return null;
    }

    if (details.schedule_type.value === RequestScheduleType.ONE_TIME) {
      return details.schedule_type;
    }

    const recurrenceType = this.recurrence()?.type;
    return recurrenceType && typeof recurrenceType !== 'string'
      ? recurrenceType
      : details.schedule_type;
  });

  readonly isWeekly = computed(
    () => this.recurrenceType() === RequestScheduleType.WEEKLY
  );

  readonly isMonthly = computed(
    () => this.recurrenceType() === RequestScheduleType.MONTHLY
  );

  readonly isYearly = computed(
    () => this.recurrenceType() === RequestScheduleType.YEARLY
  );

  readonly isMonthlyByMonths = computed(
    () =>
      this.isMonthly() &&
      this.optionValue(this.recurrence()?.by) === EVENT_MONTHLY_BY_MONTHS
  );

  readonly byDisplay = computed(() => {
    if (!this.isMonthly()) {
      return null;
    }

    const by = this.recurrence()?.by;
    if (!by) {
      return null;
    }

    if (typeof by !== 'string' && by.label) {
      return by.label;
    }

    const byValue = this.optionValue(by);
    return this.transloco.translate(
      byValue === EVENT_MONTHLY_BY_INTERVAL
        ? 'event_monthly_by_interval_label'
        : 'event_monthly_by_months_label'
    );
  });

  readonly intervalDisplay = computed(() => {
    const details = this.scheduleDetails();
    if (!details) {
      return null;
    }

    const recurrence = this.recurrence();
    if (recurrence) {
      if (this.isMonthlyByMonths() || recurrence.interval == null) {
        return null;
      }

      return this.formatInterval(recurrence.interval, this.recurrenceType());
    }

    if (!details.repeat_every) {
      return null;
    }

    return this.formatInterval(
      details.repeat_every,
      details.schedule_type?.value
    );
  });

  readonly monthsDisplay = computed(() => {
    if (!this.isMonthlyByMonths()) {
      return null;
    }

    const months = this.recurrence()?.months;
    if (!months?.length) {
      return null;
    }

    return months
      .map(monthNumber => {
        const month = EVENT_MONTHS.find(item => item.value === monthNumber);
        return month
          ? this.transloco.translate(
              `schedule_workflow_month_${month.label}_label`
            )
          : String(monthNumber);
      })
      .join(', ');
  });

  readonly daysDisplay = computed(() => {
    if (this.isWeekly()) {
      const days = this.recurrence()?.days_of_week;
      if (!days?.length) {
        return null;
      }

      return days
        .map(dayNumber => {
          const day = EVENT_WEEK_DAYS.find(item => item.value === dayNumber);
          return day
            ? this.transloco.translate(
                `schedule_workflow_week_day_${day.label}_label`
              )
            : String(dayNumber);
        })
        .join(', ');
    }

    const days = this.scheduleDetails()?.repeat_days;
    if (!days?.length) {
      return null;
    }

    return days
      .map(day =>
        this.transloco.translate(`schedule_workflow_week_day_${day}_label`)
      )
      .join(', ');
  });

  readonly repeatOnDisplay = computed(() => {
    const details = this.scheduleDetails();
    if (!details?.start_at) {
      return null;
    }

    const startDate = new Date(details.start_at);
    if (Number.isNaN(startDate.getTime())) {
      return null;
    }

    const translate = (key: string, params?: Record<string, string | number>) =>
      this.transloco.translate(key, params);

    const recurrence = this.recurrence();
    if (this.isMonthly() || this.isYearly()) {
      return formatRepeatOnDisplay(
        this.recurrenceType(),
        startDate,
        this.optionValue(recurrence?.mode),
        recurrence?.relative_option,
        recurrence?.days_of_month,
        translate
      );
    }

    if (
      details.repeat_on_by_day_of_month === null ||
      details.repeat_on_by_day_of_month === undefined
    ) {
      return null;
    }

    const scheduleType = details.schedule_type?.value;
    const options =
      scheduleType === RequestScheduleType.MONTHLY
        ? buildLegacyMonthlyRepeatOnOptions(startDate, translate)
        : scheduleType === RequestScheduleType.YEARLY
          ? buildLegacyYearlyRepeatOnOptions(startDate, translate)
          : [];

    return (
      options.find(option => option.value === details.repeat_on_by_day_of_month)
        ?.label ?? null
    );
  });

  actionsList = computed<ActionList[]>(() => {
    if (!this.isActiveSchedule()) {
      return [];
    }

    const permissions = this.userPermissions();
    const actions: ActionList[] = [];

    if (
      this.permissionsService.checkPermission(
        this.appPermissions.workflow.schedule.edit,
        permissions
      )
    ) {
      actions.push({
        key: 'edit',
        hasIcon: true,
        label: this.transloco.translate('edit_action_item'),
        isLink: true,
        routerLinks: [
          this.tenantService.tenantPath(
            `${EMPLOYEE_ROUTES.REQUESTS.DETAILS}/${this.requestId()}/${EMPLOYEE_ROUTES.REQUESTS.SCHEDULE}/${this.scheduleId()}`
          ),
        ],
      });
    }

    if (
      this.permissionsService.checkPermission(
        this.appPermissions.workflow.schedule.cancel,
        permissions
      )
    ) {
      actions.push({
        key: 'deactivate',
        hasIcon: true,
        label: this.transloco.translate('cancel_schedule_action_item'),
        labelCustomStyle: 'text-xs font-medium text-destructive-500',
      });
    }

    return actions;
  });

  constructor() {
    this.loadScheduleDetails();
  }

  private optionValue(
    option: ScheduleRecurrenceOption | null | undefined
  ): string | undefined {
    if (!option) {
      return undefined;
    }

    return typeof option === 'string' ? option : option.value;
  }

  private formatInterval(
    interval: number,
    type: string | null | undefined
  ): string {
    const unitMap: Record<string, string> = {
      [RequestScheduleType.DAILY]: this.transloco.translate(
        'schedule_workflow_unit_days'
      ),
      [RequestScheduleType.WEEKLY]: this.transloco.translate(
        'schedule_workflow_unit_weeks'
      ),
      [RequestScheduleType.MONTHLY]: this.transloco.translate(
        'schedule_workflow_unit_months'
      ),
      [RequestScheduleType.YEARLY]: this.transloco.translate(
        'schedule_workflow_unit_years'
      ),
    };

    const unit = unitMap[type ?? ''] ?? '';
    return unit ? `${interval} ${unit}` : String(interval);
  }

  goBack() {
    history.back();
  }

  onActionClick(actionKey: string) {
    if (actionKey === 'deactivate') {
      this.openCancelScheduleModal();
    }
  }

  openCancelScheduleModal() {
    const modal = {
      isOpen: true,
      hasCloseIcon: false,
      customizeContent: this.cancelScheduleModal(),
      width: '30',
    };

    this.modalService.modalContent.set(modal);
  }

  closeCancelScheduleModal() {
    this.modalService.modalContent.set({ isOpen: false });
  }

  confirmCancelSchedule() {
    this.cancelSchedule();
  }

  private loadScheduleDetails() {
    const requestId = this.requestId();
    const scheduleId = this.scheduleId();

    if (!requestId || !scheduleId) {
      this.isLoading.set(false);
      return;
    }

    this.isLoading.set(true);
    forkJoin({
      schedule: this.requestService.getRequestScheduleById(
        requestId,
        scheduleId
      ),
      requestSummary: this.requestService.getRequestSummary(requestId),
    })
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ({ schedule, requestSummary }) => {
          this.scheduleDetails.set(schedule.data);
          this.userPermissions.set(requestSummary.data.permissions ?? []);
        },
      });
  }

  private cancelSchedule() {
    const requestId = this.requestId();
    const scheduleId = this.scheduleId();
    if (!requestId || !scheduleId || this.isCancelling()) {
      return;
    }

    this.isCancelling.set(true);
    this.requestService
      .cancelRequestSchedule(requestId, scheduleId)
      .pipe(
        finalize(() => this.isCancelling.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.closeCancelScheduleModal();
          this.toaster.add(
            this.transloco.translate('schedule_details_cancel_success_message'),
            '',
            '',
            'success'
          );
          this.router.navigate([
            this.tenantService.tenantPath(EMPLOYEE_ROUTES.REQUESTS.DETAILS),
            requestId,
          ]);
        },
      });
  }
}
